import {Observable, of, throwError, throwError as observableThrowError} from 'rxjs';
import {Alert, AlertType} from './alert.model';
import {delay, switchMap} from 'rxjs/operators';
import { HttpContext, HttpContextToken, HttpErrorResponse } from '@angular/common/http';
import {environment} from '../../environments/environment';


export const api = {
  config: {
    clientId: null,
    issuer: null,
    redirectUri: null,
    realm: null,
    tenant: null,
    authServer: null,
    contextPath: null,
    subContext: '/api', // 'https://admin-tool-dev.matrixcare.me/api' so I can use Fiddler to return custom responses,
    version: '/v1',
    baseUrl: function () {
      return this.subContext;
    },
  },
  parameters: function () {
    return this.config.baseUrl() + '/environment/parameters';
  },
  thirdPartyVendorParameters: function () {
    return this.config.baseUrl() + `/environment/configuration/parameters/thirdpartyintegration`;
  },
  parametersByStack: function () {
    return this.config.baseUrl() + '/environment/parametersbystack';
  },
  stackParameters: function () {
    return this.config.baseUrl() + '/environment/stack/parameters';
  },
  currentStack: function () {
    return this.config.baseUrl() + '/environment/currentStack';
  },
  configurations: function () {
    return this.config.baseUrl() + '/environment/configurations';
  },
  configurationsByStack: function () {
    return this.config.baseUrl() + '/environment/configurationsbystack';
  },
  configurationsByCustId: function (customerId) {
    return this.config.baseUrl() + `/environment/customer/${customerId}/configurations`;
  },
  getZoneLimits: function (newLimit) {
    return this.config.baseUrl() + `/environment/configurations/zonelimits?newlimit=${newLimit}`;
  },
  parameterByKey: function (configId: string, keyName: string) {
    return this.config.baseUrl() + `/environment/configurations/${configId}/parameters?keys=${keyName}`;
  },
  parametersByPartialKey: function (configId: string, partialKey: string, includeInherited: boolean) {
    return this.config.baseUrl() + `/environment/configurations/${configId}/parameters/partialkey?partialKey=${partialKey}&includeInherited=${includeInherited}`;
  },
  parameterByKeyValueOnly: function (configId: string, keyName: string) {
    return this.config.baseUrl() + `/environment/configurations/${configId}/parameters/value?key=${keyName}`;
  },
  customers: function () {
    return this.config.baseUrl() + '/environment/customers';
  },
  customersByStack: function () {
    return this.config.baseUrl() + '/environment/customersbystack';
  },
  customersSettings: function () {
    return environment.adminToolServiceUrl + `${this.config.subContext}/customers/settings`;
  },
  conversionsEnvironment: function () {
    return this.config.baseUrl() + '/environment/conversions';
  },
  conversionsCorporations: function (envId: string) {
    return this.config.baseUrl() + `/environment/${envId}/conversions/corporations`;
  },
  conversionsFacilities: function (envId: string, corpId: string) {
    return this.config.baseUrl() + `/environment/${envId}/${corpId}/conversions/facilities`;
  },
  conversionsUnits: function (envId: string, facilityId: string) {
    return this.config.baseUrl() + `/snf/units?envId=${envId}&facilityId=${facilityId}`;
  },
  conversionsDocTypes: function (envId: string, facilityId: string) {
    return this.config.baseUrl() + `/snf/documentTypes?envId=${envId}&facilityId=${facilityId}`;
  },
  conversionsResidents: function (envId: string, facilityId: string, units: string, documentTypes: string, dates: string) {
    return this.config.baseUrl() + `/snf/residents?envId=${envId}&facilityId=${facilityId}&units=${units}&documentTypes=${documentTypes}&dates=${dates}`;
  },
  saveDocumentSession: function () {
    return this.config.baseUrl() + `/snf/redis`;
  },

  transferDocuments: function (sessionId: string) {
    return this.config.baseUrl() + `/snf/documentTransfer?sessionId=${sessionId}`;
  },

  getResidentDocumentSessions: function () {
    return this.config.baseUrl() + `/snf/getSessions`;
  },

  removeSessionDetailsFromCache: function (key: string) {
    return this.config.baseUrl() + `/snf/removeFromCacheHash?key=${key}`;
  },


  cancelSession: function () {
    return this.config.baseUrl() + `/snf/documentTransfer/cancel`;
  },
  getCurrentStackParameters: function () {
    return environment.adminToolServiceUrl + `${this.config.subContext}/parameters/currentStack`;
  },
  transactionLog: function () {
    return environment.adminToolServiceUrl + `${this.config.subContext}/transactions-log`;
  },
  users: function (provisionSL: boolean = undefined, legacy = true) {
    if (legacy) {
      if (provisionSL === undefined)
        return this.config.baseUrl() + '/ssousers';
      else
        return this.config.baseUrl() + `/ssousers?provisionSL=${provisionSL}`;
    }

    return environment.adminToolServiceUrl + `${this.config.subContext}/users`;

  },
  deepLinks: function (configId: string) {
    if (!configId)
      configId = "";

    return this.config.baseUrl() + `/deep-links?configId=${configId}`;
  },
  userTypes: function () {
    return this.config.baseUrl() + '/auxiliary/applications/user-types';
  },
  licenses: function () {
    return this.config.baseUrl() + '/attributions';
  },
  permissions: function () {
    return this.config.baseUrl() + '/permissions';
  },
  parameter: function (id) {
    return `${this.parameters()}/${id}`;
  },
  configuration: function (id) {
    return `${this.configurations()}/${id}`;
  },
  configurationDefaultParameters: function (id, subdomain) {
    /*    if (!subdomain)
          subdomain = "";*/
    if (subdomain) {
      return `${this.configuration(id)}/defaultParameters?subdomain=${subdomain}`;
    }
    return `${this.configuration(id)}/defaultParameters`;
  },
  customer: function (id) {
    return `${this.customers()}/${id}`;
  },
  user: function (id, useLegacy = true) {
    return `${this.users(undefined, useLegacy)}/${id}`;
  },
  accesses: function (id) {
    return `${this.user(id)}/accesses`;
  },
  assign: function (id) {
    return `${this.user(id)}/assign`;
  },
  applicationStatus: function (id) {
    return `${this.user(id)}/status`;
  },
  roles: function () {
    return this.config.baseUrl() + '/roles';
  },
  toggles: function () {
    return this.config.baseUrl() + '/toggles?useDefaultConfigId=true';
  },
  clientRoles: function (id) {
    return `${this.user(id)}/clients/all/roles`;
  },
  suspend: function (id) {
    return `${this.user(id, environment.useLegacyAPI)}/suspend`;
  },
  deactivate: function (id) {
    return `${this.user(id, environment.useLegacyAPI)}/deactivate`;
  },
  delete: function (id) {
    return `${this.user(id, environment.useLegacyAPI)}`;
  },
  activate: function (id) {
    return `${this.user(id, environment.useLegacyAPI)}/activate`;
  },
  activateWithPassword: function (id) {
    return `${this.user(id, environment.useLegacyAPI)}/setPassword`;
  },
  unlock: function (id) {
    return `${this.user(id, environment.useLegacyAPI)}/unlock`;
  },
  expirePassword: function (id) {
    return `${this.user(id)}/expirePassword`;
  },
  resetPassword: function (id) { // Done by admin
    return `${this.user(id)}/resetPassword`;
  },
  setTempPassword: function (id) {
    return `${this.user(id)}/setPassword`;
  },
  factor: function (id) {
    return `${this.user(id)}/factor`;
  },
  activateFactor: function (id) {
    return `${this.user(id)}/factor/activate`;
  },
  resetMyFactors: function () {
    return environment.adminToolServiceUrl + `${this.config.subContext}/users/me/reset_factors`;
  },
  convertToIDM: function (id: string) {
    return `${this.user(id)}/convertToManaged`;
  },
  convertToFederated: function (id: string) {
    return `${this.user(id)}/convertToFederated`;
  },
  resetFactors: function (ssoId: string) {
    return `${this.users()}/${ssoId}/mfa/reset_factors`;
  },
  updateFactors: function (ssoId: string) {
    return `${this.users()}/${ssoId}/mfa`;
  },

  mfa: function () {
    return this.config.baseUrl() + '/mfa';
  },
  assignAllUsersToMfaGroupStatus: function (policyId: string) {
    return `${this.users()}/mfa/assignAllUsersToMfaGroup/processCheck?policyID=${policyId}`;
  },
  bulkUpdateProcessStatus: function () {
    return `${this.users()}/mfa/bulkupdate/processCheck`;
  },

  assignAllUsersToMfaGroup: function (policyId:string,mfaToAllUsers: boolean) {
    return `${this.users()}/mfa/assignAllUsersToMfaGroup?policyID=${policyId}&mfaToAllUsers=${mfaToAllUsers}`;
  },
  getCurrentCustomerConfigurations: function () {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/customers/configurations`;
  },
  getLocationMappings: function (configId: number) {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/mappings/locations?configId=${configId}`;
  },
  getAccessMappings: function () {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/mappings/access`;
  },
  saveAccessMappings: function () {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/mappings/access`;
  },
  getCareSettings: function (configId: number) {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/customers/configurations/${configId}/care-settings`;
  },
  saveSecuritySettings: function() {
    return `${this.config.baseUrl()}/security/users`;
  },
  disableFactor: function (id) {
    return `${this.user(id)}/factor/delete`;
  },
  userDetailOptions: function () {
    return `${this.permissions()}/referenceData`;
  },
  privateDutyOfficeData: function () {
    return `${this.permissions()}/customer/pdOfficeData`
  },

  privateDutyUserOfficeData: function (configurationId: string, username: string, ssoId: string) {
    return `${this.permissions()}/user/pdOfficeData?configId=` + configurationId + '&username=' + username + '&ssoId=' + ssoId;
  },

  lpcRoleData: function(configurationId: string){
    return `${this.permissions()}/customer/lpcRoleData?configId=${configurationId}`;
  },

  customerReferenceData: function () {
    return `${this.permissions()}/customer/referenceData`;
  },
  changePassword: function (id) {
    return `${this.user(id)}/changePassword`;
  },
  securityQuestions: function () {
    return this.config.baseUrl() + '/securityQuestions';
  },
  saveUserSQ: function () {
    return `${this.users()}/securityQuestions`;
  },
  getUserSQ: function () { // This could be ssoid or username
    return `${this.users()}/securityQuestions`;
  },
  sqResetPassword: function () {
    return this.config.baseUrl() + '/sq_reset_password';
  },
  logs: function () {
    return this.config.baseUrl() + '/servicelogs/responses';
  },
  log: function (id) {
    return this.config.baseUrl() + `/servicelogs/${id}`;
  },
  traceLog: function (traceId) {
    return this.config.baseUrl() + `/servicelogs/traceid/${traceId}`;
  },
  getApiHealthchecks: function (key, rootUrl) {
    return this.config.baseUrl() + `/platform/healthchecks?key=${key}&rooturl=${rootUrl}`;
  },
  tokens: function () {
    return this.config.baseUrl() + `/tokens`;
  },
  tokensRevoke: function (id: string) {
    return this.tokens() + `/${id}`;
  },
  profileSync: function () {
    return `${this.config.baseUrl()}/users/profile/sync/snf-idm`;
  },
  profileSyncPhysicianNppa: function () {
    return `${this.config.baseUrl()}/users/profile/sync/physician-nppa-sso`;
  },
  profileSyncFailedUsers: function (processId: number) {
    return `${this.profileSync()}/${processId}/failedRecords`;
  },
  snfUsersToImport: function () {
    return `${this.config.baseUrl()}/snf/allUsers`;
  },

  snfUsersImport: function () {
    return `${this.config.baseUrl()}/snf/import`;
  },
  snfUsersImportTransactions: function () {
    return `${this.config.baseUrl()}/snf/import/transactions`;
  },
  snfUsersImportFailedUsers: function (processId: number) {
    return `${this.snfUsersImportTransactions()}/${processId}/failedRecords`;
  },
  // keycloak start
  keycloakUsersToImport: function () {
    return `${this.config.baseUrl()}/migration/keycloak/users`;
  },
  keycloakUsersImport: function () {
    return `${this.config.baseUrl()}/migration/keycloak/import`;
  },
  keycloakUsersImportTransactions: function () {
    return `${this.config.baseUrl()}/migration/keycloak/transactions`;
  },
  keycloakImportFailedUsers: function (transactionId: number) {
    return `${this.config.baseUrl()}/migration/keycloak/transactions/${transactionId}`;
  },
  keycloakCustomerReferenceData: function (crmId: string) {
    return `${this.permissions()}/customer/referenceData?crmId=${crmId}`;
  },
  corporations: function (configurationId: number) {
    return `${this.config.baseUrl()}/environment/corporations?configId=${configurationId}`;
  },
  // keycloak end
  // imports start
  importUsersTransactions: function () {
    return `${this.config.baseUrl()}/bulkimport/transactions`;
  },
  importFailedUsers: function (transactionId: number) {
    return `${this.config.baseUrl()}/bulkimport/transactions/${transactionId}`;
  },
  uploadImportUsers: function() {
    return `${this.config.baseUrl()}/bulkimport/upload/users`;
  },
  usersImport: function () {
    return `${this.config.baseUrl()}/bulkimport/users`;
  },
  downloadImportsTemplate: function (processId: number) {
    return `${this.config.baseUrl()}/bulkimport/${processId}/csv`;
  },
  // imports end
  usersStatus: function () {
    return `${this.users(null, false)}/status`;

  },
  parametersByConfigId: function (configurationId: number) {
    return `${this.config.baseUrl()}/environment/parameters?configId=${configurationId}`;
  },
  parametersBulkUpdate: function (configurationId: number) {
    return `${this.config.baseUrl()}/environment/parameters?configId=${configurationId}`;
  },
  parametersBulkDelete: function (parameterIds: string) {
    return `${this.config.baseUrl()}/environment/parameters?parameterIds=${parameterIds}`;
  },
  addBatchParameters: function () {
    return `${this.config.baseUrl()}/environment/parameters/addbatch`;
  },
  addMyMatrixParameter: function () {
    return `${this.config.baseUrl()}/environment/parameter/addmymatrix`;
  },
  userManagementHistoryLog: function () {
    return `${this.config.baseUrl()}/user-history`;
  },
  configurationZones: function () {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/zones`;
  },
  configurationZonesActivate: function (id: string) {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/zones/${id}/activate`;
  },
  configurationZonesDeactivate: function (id: string) {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/zones/${id}/deactivate`;
  },
  userManagementNoSecurityQuestionsSet: function () {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/reports/users-no-security-questions-set`;
  },
  userManagementExportUsers: function () {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/reports/export-users`;
  },
  configurationRules: function () {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/rules`;
  },
  getPolicyStatus: function (policyId:string) {
    return `${this.configurationPolicy(policyId)}/status`;
  },
  configurationRulesReferenceData: function () {
    return `${this.configurationPolicies()}/referenceData`;
  },
  configurationRule: function (policyId:string, id: string) {
    return `${this.configurationPolicy(policyId)}/rules/${id}`;
  },
  configurationRulesActivate: function (id: string) {
    return `${this.configurationRule(id)}/activate`;
  },
  reorderRule: function (ruleId: string, priority: number) {
    return `${this.configurationRule(ruleId)}/reorder/${priority}`;
  },
  reorderPolicy: function (policyId: string, priority: number) {
    return `${this.configurationPolicies()}/${policyId}/reorder/${priority}`;
  },
  configurationRulesDeactivate: function (id: string) {
    return `${this.configurationRule(id)}/deactivate`;
  },
  downloadMfaBulkUpdateTemplate: function () {
    return `${this.users()}/mfa/bulkupdate/template`;
  },
  uploadMfaBulkUpdate: function () {
    return `${this.users()}/mfa/bulkupdate`;
  },
  customerConfigurationParameters: function (configurationId: number) {
    return `${this.config.baseUrl()}/environment/customer/configuration/${configurationId}/parameters`;
  },
  getCustomerDefaultConfigCount: function (customerId: number) {
    return `${this.config.baseUrl()}/environment/customer/${customerId}/configurations/defaultcount`;
  },
  getCustomerParameters: function (customerId: number) {
    return environment.adminToolServiceUrl + `${this.config.subContext}/environment?customerId=${customerId}`;
  },
  getMfaSettings: function () {
    return environment.adminToolServiceUrl + `${this.config.subContext}/mfa`;
  },
  mfaDisable: function () {
    return environment.adminToolServiceUrl + `${this.config.subContext}/mfa/enroll-disable`;
  },
  mfaEnroll: function () {
    return environment.adminToolServiceUrl + `${this.config.subContext}/mfa/enroll-enable`;
  },
  mfaProductAssignment: function () {
    return `${this.users()}/mfa/product-assignment`;
  },

  userProvisionedForApp(ssoId: string, application: string) {
    return `${this.users()}/${ssoId}/userprovisionedforapp?` + `app=` + application;
  },
  supportCustomers: function () {
    return environment.adminToolServiceUrl + `${this.config.subContext}/support/customers`;
  },
  launcher: function (path:string) {
    return environment.adminToolServiceUrl + `${this.config.subContext}${path}`;

  },
  deleteLauncherContext: function (configId:string) {
    return this.config.baseUrl() + `/environment/configuration?collection=launcher:${configId}:*`;
  },
  // okta shell
  getOktaShellDetails: function (crmId: string) {
    return `${this.config.baseUrl()}/idm/configurations/${crmId}`;
  },
  postOktaShellDetails: function () {
    return `${this.config.baseUrl()}/idm/configurations`;
  },
  hotReload: function () {
    return this.config.baseUrl() + '/environment/configuration/parameters/hotreload';
  },
  stackHotReload: function () {
    return this.config.baseUrl() + '/environment/stack/parameters/hotreload';
  },
  serviceLogoutUrl: function () {
    return '/api/idm/logout';
  },
  resetElasticDb: function (snfApiUrl:string,configId:string) {
    return this.config.baseUrl() + `/environment/configuration/globalsearch/resetelastic?snfApiUrl=${snfApiUrl}&configId=${configId}`;
  },
  getMcmdRoles: function () {
    return `${this.config.baseUrl()}/multicare/roles`;
  },
  getMcmdRoleById: function (roleId: number) {
    return `${this.config.baseUrl()}/multicare/roles/${roleId}`;
  },
  configurationPolicies: function () {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/signon-policies`;
  },
  configurationPolicy: function (policyId: string) {
    return `${this.configurationPolicies()}/${policyId}`;
  },
  configurationPolicyActivate: function (policyId: string) {
    return `${this.configurationPolicies()}/${policyId}/activate`;
  },
  configurationPolicyDeactivate: function (policyId: string) {
    return `${this.configurationPolicies()}/${policyId}/deactivate`;
  },
  getApplications: function () {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/idp-applications`;
  },
  getSessions: function () {
    return `${environment.adminToolServiceUrl}${this.config.subContext}/sessions`;
  },
  getSignOnPolicies: function () {
    return environment.adminToolServiceUrl + `${this.config.subContext}/signon-policies`;
  },
  configurationFeaturesByConfigId: function (configId: string) {
    if (!configId)
      configId = "";
    return this.config.baseUrl() + `/configuration-features?configId=${configId}`;
  },
  getRules: function () {
    return environment.adminToolServiceUrl + `${this.config.subContext}/rules`;
  },
  syncCisData: function (configId: string, username: string) {
    return this.config.baseUrl() + `/environment/configuration/cis/asyncupload/snf?configId=${configId}&userName=${username}`;
  },
  configurationGuidSync: function (configId:string) {
    return this.config.baseUrl() + `/environment/configuration/guid/sync?configId=${configId}`;
  },
  customerGuidSync: function (crmId:string) {
    return this.config.baseUrl() + `/environment/customer/guid/sync?crmId=${crmId}`;
  },
  corporateStructureByConfigId: function (configId: string) {
    return this.config.baseUrl() + `/customers/corporate-structure/${configId}`;
  },
  corporateStructureByCrmId: function (crmId: string, cacheOnly = false) {
    const base = this.config.baseUrl() + `/customers/${crmId}/corporate-structure`;
    return cacheOnly ? base + '?cacheOnly=true' : base;
  },
  customerConfigurations: function (crmId: string) {
    return this.config.baseUrl() + `/customers/${crmId}/configurations`;
  },
  getPasswordPolicySettings: function () {
    return this.config.baseUrl() + `/password-policies`;
  },
  savePasswordPolicySettings: function(policyId: string) {
    return this.config.baseUrl() + `/password-policies/${policyId}`;
  }
}


///This retry strategy can be used by the retry {delay:xxxx}, since retryWhen is being depracated.
export function newRetryStrategy({ attempts= 3, delayMs= 1500 } = {})  {

  let retries = attempts - 1;
  return (error: HttpErrorResponse, retryCount: number) => {
    return of(error).pipe(
      switchMap(error => shouldRetry(error) && retries-- > 0 ? of(error) : throwError(()=>error)),
      delay(delayMs));

  };

}

export function retryStrategy({ attempts = 3, delayMs = 1500 } = {}) {
  let retries = attempts - 1;

  return (errors: Observable<HttpErrorResponse>) => {
    return errors.pipe(
      switchMap(error => shouldRetry(error) && retries-- > 0 ? of(error) : throwError(error)),
      delay(delayMs),
    );

  };
}

export function retryStrategyFor404({ attempts = 3, delayMs = 1500 } = {}) {
  let retries = attempts - 1;

  return (errors: Observable<HttpErrorResponse>) => {
    return errors.pipe(
      switchMap(error => shouldRetry404(error) && retries-- > 0 ? of(error) : throwError(error)),
      delay(delayMs)
    );

  };
}


function shouldRetry(error: HttpErrorResponse): boolean {
  if (error && error.status) {
    const status = error.status;
    const initial = Math.floor(status / 100);
    const remainder = status % 100;
    // Check 400 statuses
    // 408: Request Timeout - can retry
    // 4xx - everything else: should not retry
    if (initial === 4 && remainder !== 8) {
      return false;
      // Check 500 statuses
      // 500: Internal Server Error - can retry
      // 501: Not Implemented - should not retry
      // 502: Bad Gateway - should not retry
      // 503: Service Unavailable - can retry
      // 504: Gateway Timeout - can retry
      // 505: HTTP Version Not Supported - should not retry
    } else if (initial === 5 && [1, 2, 5].indexOf(remainder) > -1) {
      return false;
    }
  }
  return true;
}


function shouldRetry404(error: HttpErrorResponse): boolean {
  if (error && error.status) {
    const status = error.status;
    const initial = Math.floor(status / 100);
    const remainder = status % 100;
    // Check 404 statuses
    if (status == 404) {
      return true;
    }
  }
  return false;
}

export const isSuccess = (status: number): boolean => (status >= 200 && status < 300);

export function handleError(error: Response): Observable<any> {
  let json;
  try {
    if (error.json()) {
      json = error.json();
      if (!json.status) {
        json.status = error.status;
      }
    }
  } catch (e) {
    console.error('error occurred while parsing the error response as json. Json not available in the response.');
  }
  return observableThrowError(json || error);
}



export function getAlertMessage(errorObject: any): Alert {
  /*ugly fix*/
  if (errorObject.status === 401) {
      window.location.href = '/logout';
  }

  let error: any = 'Unknown error occurred';
  let status = undefined;
  if (!!errorObject) {
    if (errorObject instanceof Response) {
      status = errorObject.status;
      error = errorObject.text();
    } else if (errorObject instanceof ProgressEvent && errorObject.type === 'error') {
      error = 'Request timed out at server';
    } else {
      error = errorObject.error || errorObject;
      status = error.status || errorObject.status;
    }
    error = getErrorMessageForCodes(status, error);
  }
  return new Alert(AlertType.DANGER, error);
}

function getErrorMessage(error: any) {

  if (typeof error === 'string' && error.trim()[0] == '{') {
    try {
      error = JSON.parse(error);
    } catch (e) { //ignore parsing error just in case is not a really json object
    }
  }

  let message = error;
  if (error.message && error.message !== 'No message available') {
    message = error.message;
  } else if (error.description) {
    message = error.description;
  } else if (error.error) {
    message = error.error;
  } else if (error[""] && error[""].length && error[""][0]) {//bad requests handled by the framework
    message = error[""][0];
  } else if (error.errors && error.errors) {//bad requests handled by the .net framework
    message = "";
    let keys = Object.keys(error.errors);
    for (let key of keys) {
      message += error.errors[key] + ". ";
    }
  }
  return message;
}

function getErrorMessageForCodes(status: number, message?: string) {
    switch (status) {
      case 401:
        return 'You\'re not authorized access this resource';
      case 404:
        return 'Requested resource not found';
      case 500 :
        return 'This page is temporarily unavailable, please try again later. If the issue still persists, please contact support.';
      case 0:
        return 'This page is temporarily unavailable, please try again later. If the issue still persists, please contact support.';
      case 504:
        return 'Request timeout. Please contact support if you keep getting this error.';
    }

    return getErrorMessage(message);

}


//retry interceptor is not used yet
export const SKIP_RETRY_TOKEN = new HttpContextToken<boolean>(() => false); //default .skip the retries.

export function retryHttpContext(retry = false) {
  return new HttpContext().set(SKIP_RETRY_TOKEN, !retry);
}
